import { Close, HelpOutline } from '@mui/icons-material';
import {
    Dialog,
    Toolbar,
    Typography,
    IconButton,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    AlertTitle,
    Tooltip,
} from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useState, useEffect } from 'react';
import {
    ThirdPartyBackOfficeCustomerInfo,
    ThirdPartyBackOfficeInfo,
    ThirdPartyBackOfficeTankInfo,
    Device,
} from 'types';
import styles from './BackofficePullModal.module.scss';

export interface BackofficePullModalProps {
    open: boolean;
    onClose: () => void;
    /** Customer-facing display name of the third-party backoffice provider. */
    backofficeLabel: string;
    tankInfo: ThirdPartyBackOfficeTankInfo;
    customerInfo: ThirdPartyBackOfficeCustomerInfo;
    device: Device;
    /** Callback to alert parent component that user confirmed the backoffice import. */
    onConfirmImport: React.Dispatch<React.SetStateAction<boolean>>;
    /** The latest account number the user has typed into the form. May or may not be saved yet.*/
    newAccountNumber?: string;
    /** The latest tank number the user has typed into the form. May or may not be saved yet.*/
    newTankNumber?: string;
    /** The account number as currently saved on the device object. */
    oldAccountNumber?: string;
    /** The tank number as currently saved on the device object. */
    oldTankNumber?: string;
    /** The specific name the particular third-party backoffice gives to the account number field. */
    accountNumberLabel?: string;
    /** The specific name the particular third-party backoffice gives to the tank number field. */
    tankNumberLabel?: string;
}

/** Read-only modal. Displays the latest values pulled from third party backoffice after a sync is complete. */
export const BackofficePullModal = ({
    open,
    onClose,
    backofficeLabel,
    tankInfo,
    customerInfo,
    device,
    onConfirmImport,
    newAccountNumber,
    newTankNumber,
    accountNumberLabel,
    tankNumberLabel,
    oldTankNumber,
    oldAccountNumber,
}: BackofficePullModalProps) => {
    const [backofficeInfo, setBackofficeInfo] =
        useState<ThirdPartyBackOfficeInfo>();

    // Set default form values from tank and customer inputs.
    useEffect(() => {
        if (tankInfo && customerInfo) {
            setBackofficeInfo({
                customer_name: customerInfo.customer_name,
                tank_name: tankInfo.tank_name,
                address: tankInfo.address,
                capacity: tankInfo.capacity,
                delivery_type: tankInfo.delivery_type,
                group: tankInfo.group,
            });
        }
    }, [tankInfo, customerInfo]);

    const confirmImport = () => {
        onConfirmImport(true);
        onClose();
    };

    const cancel = () => {
        onConfirmImport(false);
        onClose();
    };

    return (
        <div
            data-testid="BackofficePullModal"
            className={styles.BackofficePullModal}
        >
            <Dialog
                open={open}
                onClose={onClose}
                className={styles.BackofficePullModal}
                maxWidth="md"
                fullWidth={true}
            >
                <Toolbar>
                    <Typography className={styles.modalHeader} variant="h6">
                        Imported BackOffice Information
                    </Typography>
                    <IconButton
                        color="inherit"
                        onClick={() => onClose()}
                        aria-label="close"
                    >
                        <Close />
                    </IconButton>
                </Toolbar>
                <DialogContent>
                    <Alert severity="info" sx={{ marginBottom: '12px' }}>
                        <AlertTitle>
                            This information was imported from {backofficeLabel}
                            , complete the import to replace the current config
                            data. Cancel to keep the current config.
                        </AlertTitle>
                    </Alert>

                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Field</TableCell>
                                    <TableCell>Old Value</TableCell>
                                    <TableCell>New Imported Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow
                                    className={
                                        oldTankNumber !== newTankNumber
                                            ? styles.isDifferent
                                            : ''
                                    }
                                >
                                    <TableCell className={styles.fieldName}>
                                        {tankNumberLabel}
                                    </TableCell>
                                    <TableCell>{oldTankNumber}</TableCell>
                                    <TableCell>{newTankNumber}</TableCell>
                                </TableRow>

                                <TableRow
                                    className={
                                        oldAccountNumber !== newAccountNumber
                                            ? styles.isDifferent
                                            : ''
                                    }
                                >
                                    <TableCell className={styles.fieldName}>
                                        {accountNumberLabel}
                                    </TableCell>
                                    <TableCell>{oldAccountNumber}</TableCell>
                                    <TableCell>{newAccountNumber}</TableCell>
                                </TableRow>

                                <TableRow
                                    className={
                                        device.customer_name !==
                                        backofficeInfo?.customer_name
                                            ? styles.isDifferent
                                            : ''
                                    }
                                >
                                    <TableCell className={styles.fieldName}>
                                        Customer Name
                                    </TableCell>
                                    <TableCell>
                                        {device.customer_name}
                                    </TableCell>
                                    <TableCell>
                                        {backofficeInfo?.customer_name}
                                    </TableCell>
                                </TableRow>

                                <TableRow
                                    className={
                                        device.name !==
                                        backofficeInfo?.tank_name
                                            ? styles.isDifferent
                                            : ''
                                    }
                                >
                                    <TableCell className={styles.fieldName}>
                                        Tank Name
                                    </TableCell>
                                    <TableCell>{device.name}</TableCell>
                                    <TableCell>
                                        {backofficeInfo?.tank_name}
                                    </TableCell>
                                </TableRow>

                                <TableRow
                                    className={
                                        device.address !==
                                        backofficeInfo?.address
                                            ? styles.isDifferent
                                            : ''
                                    }
                                >
                                    <TableCell className={styles.fieldName}>
                                        Address
                                    </TableCell>
                                    <TableCell>{device.address}</TableCell>
                                    <TableCell>
                                        {backofficeInfo?.address}
                                    </TableCell>
                                </TableRow>

                                <TableRow
                                    className={
                                        device.capacity !==
                                        backofficeInfo?.capacity
                                            ? styles.isDifferent
                                            : ''
                                    }
                                >
                                    <TableCell className={styles.fieldName}>
                                        Capacity
                                    </TableCell>
                                    <TableCell>{device.capacity}</TableCell>
                                    <TableCell>
                                        {backofficeInfo?.capacity}
                                    </TableCell>
                                </TableRow>

                                {backofficeLabel.toLowerCase() === 'cargas' && (
                                    <>
                                        <TableRow
                                            data-testid="CargasGroup"
                                            className={
                                                device.group !==
                                                backofficeInfo?.group
                                                    ? styles.isDifferent
                                                    : ''
                                            }
                                        >
                                            <TableCell
                                                className={styles.fieldName}
                                            >
                                                Group
                                            </TableCell>
                                            <TableCell>
                                                {device.group}
                                            </TableCell>
                                            <TableCell>
                                                {backofficeInfo?.group}
                                            </TableCell>
                                        </TableRow>

                                        <TableRow
                                            data-testid="CargasDeliveryType"
                                            className={
                                                device.delivery_type !==
                                                backofficeInfo?.delivery_type
                                                    ? styles.isDifferent
                                                    : ''
                                            }
                                        >
                                            <TableCell
                                                className={styles.fieldName}
                                            >
                                                Delivery Type
                                            </TableCell>
                                            <TableCell>
                                                {device.delivery_type}
                                            </TableCell>
                                            <TableCell>
                                                {backofficeInfo?.delivery_type}
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button
                        aria-label="cancel"
                        variant="text"
                        color="primary"
                        onClick={cancel}
                    >
                        Cancel
                        <Tooltip
                            title={
                                'Cancel backoffice sync import. New imported data will not overwrite existing values.'
                            }
                            enterTouchDelay={0}
                            placement="top"
                            className={styles.buttonTooltip}
                        >
                            <HelpOutline color="inherit" fontSize="small" />
                        </Tooltip>
                    </Button>
                    <Button
                        aria-label="confirm"
                        variant="contained"
                        color="primary"
                        onClick={confirmImport}
                    >
                        Complete Import
                        <Tooltip
                            title={
                                'Complete backoffice sync import. New imported data will overwrite and replace existing values.'
                            }
                            enterTouchDelay={0}
                            placement="top"
                            className={styles.buttonTooltip}
                        >
                            <HelpOutline color="inherit" fontSize="small" />
                        </Tooltip>
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
